import ContextMenu from '@/components/common/ContextMenu';
import InstrumentInfoContext from '@/components/common/InstrumentInfoContext';
import Loader from '@/components/common/Loader';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { ProfitLossData, SummaryResponse } from '@/types/sso';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalPositionTable from '@/utils/hooks/usePortalPositionTable';
import { memo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import SafeInput from '../inputs/SafeInput';
import PortalVirtualizedTable from '../table/PortalVirtualizedTable';
import PositionGroupSwitch, { GroupBy } from '../trader-modules/Blotters/PositionGroupSwitch';

export interface PortalPositionRow extends ProfitLossData {}

export interface PortalPositionTableProps {
    summary?: SummaryResponse;
    baseCurrency: string;
    isHistoric?: boolean;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalPositionTable = (props: PortalPositionTableProps) => {
    const { isHistoric = false } = props;

    const lang = useLanguage();
    const contextMenu = useContextMenu();
    const currentAccount = useAppSelector(selectCurrentAccount);

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        contextMenu;

    const [groupBy, setGroupBy] = useState<GroupBy>('Products');
    const [stateLoading, setStateLoading] = useState<boolean>(false);
    const [instrumentInfoContext, setInstrumentInfoContext] = useState();

    const { debouncedTableInstance, columnSizing, sorting, formattedPortalPositions, expandedRows, globalFilter } =
        usePortalPositionTable({
            ...props,
            groupBy,
            setInstrumentInfoContext,
            handleOpenContextMenu
        });

    useDidUpdate(() => {
        setStateLoading(true);
        setTimeout(() => setStateLoading(false), 1000);
    }, [currentAccount]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-4 sm:p-6 py-4 pb-6 border border-brand-neutral-700 text-brand-text rounded-md overflow-hidden max-h-[750px]">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-wrap gap-2 justify-between">
                    <div className="">
                        {isHistoric ? lang.portal.historicOpenPositions : lang.portal.currentOpenPositions}
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-wrap-reverse">
                        <div className="flex gap-2 whitespace-nowrap">
                            <div className="relative flex items-center">
                                <MdSearch className="absolute left-2 text-brand-neutral-400 z-10 w-5 h-5" />
                                <SafeInput
                                    className="w-48 text-xs rounded-md pl-8"
                                    placeholder="Search..."
                                    value={globalFilter || ''}
                                    onChange={(e) => debouncedTableInstance.setGlobalFilter(e.target.value)}
                                />
                            </div>
                        </div>
                        <PositionGroupSwitch groupBy={groupBy} setGroupBy={setGroupBy} className="rounded-md" />
                    </div>
                </div>
                {stateLoading ? (
                    <div className="m-2 mt-0">
                        <Loader />
                    </div>
                ) : (
                    <MemoizedPortalPositionTable
                        expandedRows={expandedRows}
                        globalFilter={globalFilter}
                        tableInstance={debouncedTableInstance}
                        columnSizing={columnSizing}
                        sorting={sorting}
                        formattedPortalPositions={formattedPortalPositions}
                    />
                )}
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                offset={{ x: 4, y: 4 }}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}
                className="!bg-brand-background !w-auto">
                <InstrumentInfoContext market={instrumentInfoContext} handleCloseContextMenu={handleCloseContextMenu} />
            </ContextMenu>
        </div>
    );
};

export default PortalPositionTable;

const MemoizedPortalPositionTable = memo(({ tableInstance }: any) => {
    // globalFilterEnabled
    return <PortalVirtualizedTable tableInstance={tableInstance} isLoadingTableContent={false} />;
});

MemoizedPortalPositionTable.displayName = 'PortalPositionTable';
